/**
 * The appearance switch: one button that steps device → light → dark.
 *
 * Three states in one control rather than a menu, because it sits in bars that
 * are already full on a phone. The icon shows the *choice* (a half-filled circle
 * for "match device"), and the label says both the choice and that pressing
 * changes it.
 */
import { useSyncExternalStore } from 'react'

import { useT } from '../lib/i18n/I18nProvider'
import {
  type ThemePreference,
  getThemePreference,
  nextThemePreference,
  setThemePreference,
  subscribeTheme,
} from '../lib/theme'

export function ThemeSwitch({ className = '' }: { className?: string }) {
  const t = useT()
  const preference = useSyncExternalStore(subscribeTheme, getThemePreference, getThemePreference)
  const names: Record<ThemePreference, string> = {
    system: t.common.themeSystem,
    light: t.common.themeLight,
    dark: t.common.themeDark,
  }
  const label = t.common.themeSwitch(names[preference])

  return (
    <button
      type="button"
      className={`theme-switch ${className}`.trim()}
      onClick={() => setThemePreference(nextThemePreference(preference))}
      aria-label={label}
      title={label}
      data-preference={preference}
    >
      <ThemeIcon preference={preference} />
    </button>
  )
}

function ThemeIcon({ preference }: { preference: ThemePreference }) {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' } as const
  if (preference === 'light') {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <circle cx="12" cy="12" r="4" {...stroke} />
        <path
          d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4"
          {...stroke}
        />
      </svg>
    )
  }
  if (preference === 'dark') {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path d="M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10z" {...stroke} strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <circle cx="12" cy="12" r="8" {...stroke} />
      <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
    </svg>
  )
}
